/**
 * @file THE FACADE — one mixer, one backend, joined. This is what the rest of
 * the game calls, and it is the ONLY file in the audio system that both halves
 * can see: `mixer.js` decides which sound at what gain and has never heard of a
 * node; `AudioBackend.js` makes a noise and has never heard of a slug.
 *
 * The mould is `Gameplay`'s join of `systems/Speech.js` (model) and
 * `render/SpeechBubbles.js` (pixels), one layer down and in one factory.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ NO TABLE ⇒ NO SYSTEM. THAT IS THE GATE, AND IT IS BY CONSTRUCTION.
 * ─────────────────────────────────────────────────────────────────────────────
 * `src/game/sfx.js` is CONTENT and the engine never imports it — `PlayRoute.jsx`
 * builds it and hands it down, exactly like `createWeaponTable()`,
 * `createFactionRules()` and `createDialogueScript()`. With no table injected,
 * `createSfx({})` builds **no mixer, no backend, no `AudioContext`, no
 * subscription and no dice**, and every method below returns safely without
 * touching anything. That is what keeps hero mode's golden checksum
 * **1878938342** still, and what lets a node suite construct this object.
 *
 * ⚠ The gate is a single `enabled` flag read at the top of every method, NOT a
 * caller remembering to check. `docs/weapons.md` records the same shape for
 * `src/game/weapons.js` and the same reason.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE CLOCK IS THE AUDIO CLOCK, NOT AN ACCUMULATOR
 * ─────────────────────────────────────────────────────────────────────────────
 * `update(dt)` is driven from the engine's RENDER hook, not the fixed step,
 * because **loops must survive a frozen sim** — five overlays can halt
 * `Level#update` (`ui/hud/freezeOwner.js`), and rain that stops because a shop
 * panel is open is a bug. So `dt` is never assumed to be 1/60, and nothing here
 * integrates it if it can avoid it: once the context exists the clock is
 * `AudioContext.currentTime`, which is the same clock every ramp is scheduled
 * against and cannot drift from it by construction. `dt` only drives the
 * fallback clock used before the context exists, and the two are spliced with a
 * fixed offset so the clock the mixer sees is MONOTONIC across the handover — a
 * clock that jumps backwards makes every `minGap` in the table misfire at once.
 *
 * Loop FADES are likewise not stepped here: they are `linearRampToValueAtTime`
 * schedules in the audio graph, so they complete correctly whatever the frame
 * rate does, including at zero frames.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ A VOICE ID IS REUSED, SO EVERY VOICE CARRIES ITS RECORD'S IDENTITY
 * ─────────────────────────────────────────────────────────────────────────────
 * The mixer's voice ids are POOL SLOTS: it steals the lowest-priority live voice
 * when the pool is full and hands the same id straight back out. That is the
 * `VehicleDamage` `(handle, serial)` hazard exactly — a stale `onEnded` arriving
 * after the slot has been re-let would free a voice that belongs to somebody
 * else. Hence {@link records}: a slot maps to ONE record object, an ending voice
 * only releases the slot if the record still in the map is its own, and
 * installing into an occupied slot stops the previous voice first (the steal the
 * mixer just decided on, made audible).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ `sfx.music` IS A THIRD PATH, NOT A LOUD LOOP
 * ─────────────────────────────────────────────────────────────────────────────
 * The radio STREAMS; everything else above decodes. Three minutes of 48 kHz
 * stereo is ~69 MB of `Float32Array` once `decodeAudioData` has had it, so five
 * tracks would cost more memory than the rest of the game put together — the long
 * version is in `AudioBackend.js`'s header. So music takes no mixer slot, no
 * priority, no distance law and no pool entry, and none of the book-keeping in
 * this file reaches it: `update()` does not touch it, `stopAllLoops` does not stop
 * it, a steal cannot take it and `sweepStolen` cannot see it. What it DOES share
 * is the bus ladder — `music` is a level exactly like `sfx`, `ambient` and `ui`,
 * so the settings screen and the master mute reach it for free.
 *
 * ⚠ THE GATE APPLIES TO IT TOO. With no sound table there is no backend and no
 * `AudioContext`, so there is nowhere for a stream to go: every `music` method is
 * a no-op and every getter reads empty. A radio table without a sound table is not
 * a configuration this game ships.
 *
 * @module engine/audio/Sfx
 */

import { createAudioBackend } from './AudioBackend.js';
import { createMixer, VOICE } from './mixer.js';

/**
 * Where the clips live, when the caller does not say. ⚠ The generated manifest
 * exports `AUDIO_BASE` next to `SFX_ASSETS`, but `createSoundTable()` does not
 * carry it, and **the engine may not import `src/game/`** — so the default is
 * duplicated here as a literal. It is a PATH, not tuning, and the two are tied
 * by nothing; if `tools/build-sfx.mjs` is ever pointed at another directory,
 * pass `base` in.
 */
const DEFAULT_BASE = '/audio';

/** Bus levels when neither the table nor the caller says. */
const DEFAULT_LEVELS = Object.freeze({ master: 1, sfx: 1, ambient: 1, ui: 1, music: 1 });

/** Seconds a loop's live gain/pan takes to reach a new value. See {@link refreshLoop}. */
const TRACK_RAMP = 0.06;

/**
 * Seconds a STOLEN voice takes to disappear. Short enough to be the steal the
 * mixer asked for, long enough not to be a click — the one thing shorter than
 * this is a discontinuity.
 */
const STEAL_FADE = 0.02;

/** Seconds a bus level takes to move. A slider is a stream of small steps. */
const LEVEL_RAMP = 0.03;

/** Smallest change worth writing to the graph. Below it the ramp is inaudible. */
const EPS = 1e-3;

/** Shared empty opts, so the hot path allocates nothing. */
const NO_OPTS = Object.freeze({});

/** @param {any} n @returns {boolean} */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/** @param {any} v @param {number} d @returns {number} */
const num = (v, d) => (usable(v) ? v : d);

/**
 * @typedef {object} VoiceRec
 * @property {number} voice The mixer's slot id — see the header on reuse.
 * @property {string} slug
 * @property {string} url
 * @property {any} v The backend voice.
 * @property {string|null} loopId `slug|key`, or null for a one-shot.
 * @property {boolean} positional
 * @property {number} x
 * @property {number} y
 * @property {number} z
 * @property {number} userGain The caller's own multiplier, re-applied per frame.
 * @property {number} gain Last gain WRITTEN, so a still loop writes nothing.
 * @property {number} pan Last pan written.
 * @property {number} endsAt When the SOURCE really runs out, on this file's
 *   clock, measured from the decoded buffer. `Infinity` for a loop. See
 *   {@link sweepStolen} — it is a discriminator, not a schedule.
 * @property {boolean} fading Stopping; `update` must not fight its fade.
 */

/**
 * A sound is POSITIONAL when x AND z are both real numbers — ⚠ **the mixer's
 * definition, mirrored deliberately** (`request` step b, `gainPanFor`). A
 * looser test here (an `||`, say) would have this file re-evaluating a loop's
 * distance every frame for a sound the mixer allocated as 2D, and the two would
 * disagree about the pan of a sound nobody can hear moving.
 * @param {any} op @returns {boolean}
 */
const isPositional = (op) => usable(op?.x) && usable(op?.z);

/**
 * Build the sound system.
 *
 * @param {object} [o]
 * @param {any} [o.table] The object from `createSoundTable()`. ⚠ **REQUIRED for
 *   anything to happen** — see the header. `{assets, mix, tune, levels}`.
 * @param {Record<string, number>|null} [o.levels] Starting bus levels, overriding
 *   the table's. This is where a persisted settings screen injects itself.
 * @param {(() => void)|null} [o.onReady] Fired once when the context is running
 *   and the first decode tier has landed.
 * @param {((msg: string, err?: any) => void)|null} [o.log] Warnings.
 * @param {string} [o.base] Clip directory. See {@link DEFAULT_BASE}.
 * @param {number} [o.seed] Level seed, forwarded to the mixer's private stream.
 *   ⚠ That stream is NOT a sim stream — nothing in the simulation may read it,
 *   which is what stops a variant choice moving the hero checksum.
 * @returns {object} The `SfxApi` from the audio contract.
 */
export function createSfx(o = {}) {
  const {
    table = null,
    levels = null,
    onReady = null,
    log = null,
    base = DEFAULT_BASE,
    seed = 0,
  } = o ?? {};

  const mix = table?.mix ?? null;
  const assets = table?.assets ?? null;
  const tune = table?.tune ?? null;

  /**
   * ⚠ THE GATE. Every method's first line. With no content there is nothing to
   * play, so there is no backend, no context and no mixer to build.
   */
  const enabled = !!(mix && assets && typeof mix === 'object' && typeof assets === 'object');

  /** Bus levels live here whether or not there is a graph to project them onto. */
  const level = {
    master: num(levels?.master ?? table?.levels?.master, DEFAULT_LEVELS.master),
    sfx: num(levels?.sfx ?? table?.levels?.sfx, DEFAULT_LEVELS.sfx),
    ambient: num(levels?.ambient ?? table?.levels?.ambient, DEFAULT_LEVELS.ambient),
    ui: num(levels?.ui ?? table?.levels?.ui, DEFAULT_LEVELS.ui),
    // ⚠ The RADIO's own level (`RADIO_TUNE.gain`) is NOT this. This is the
    // player's Music slider; that is the track gain, one node further in.
    music: num(levels?.music ?? table?.levels?.music, DEFAULT_LEVELS.music),
  };

  const loopFade = Math.max(0.01, num(tune?.loopFade, 0.25));

  const backend = enabled
    ? createAudioBackend({ levels: level, onReady, log })
    : null;
  const mixer = enabled
    ? // ⚠ `assets` is passed even though the contract's `createMixer` signature
      // lists only `{mix, tune, seed}`: `mixer.update(now)` is specified to reap
      // expired one-shots and "dur is known from assets", which it can only be
      // if it is handed them. An extra key a factory does not destructure is
      // free; the alternative is a reaper with no clip lengths.
      createMixer({ mix, tune, assets, seed })
    : null;

  // ── state ───────────────────────────────────────────────────────────────────

  /** @type {Map<number, VoiceRec>} Mixer slot id → the record that owns it NOW. */
  const records = new Map();
  /** @type {Map<string, VoiceRec>} `slug|key` → the LIVE (not fading) loop. */
  const loops = new Map();

  /** The listener, mirrored so a 2D sound can ask the distance law for "here". */
  const lis = { x: 0, y: 0, z: 0, yaw: 0 };

  let muted = false;
  let disposed = false;

  /** Fallback clock, seconds. Only authoritative before the context exists. */
  let simT = 0;
  /** Offset that splices the audio clock onto {@link simT}. See the header. */
  let ctxOff = 0;
  let ctxAdopted = false;

  /** @param {string} m @param {any} [e] */
  const warn = (m, e) => {
    if (typeof log === 'function') log(m, e);
  };

  /**
   * @returns {number} A MONOTONIC seconds clock. `AudioContext.currentTime` once
   *   there is one — see the header on why an accumulator is the wrong answer.
   */
  const now = () => {
    const ct = backend?.time ?? null;
    if (ct === null) return simT;
    if (!ctxAdopted) {
      ctxAdopted = true;
      ctxOff = simT - ct; // the splice: the clock never steps backwards
    }
    return ct + ctxOff;
  };

  // ── the content lookups ─────────────────────────────────────────────────────

  /**
   * @param {string} slug
   * @param {any} variant
   * @param {any} key
   * @returns {string} The clip URL, or '' when the slug has no clips.
   *   ⚠ `def.src` — the alias is resolved in CONTENT (`createSoundTable`), so
   *   `explosion` reaches `car_crash`'s buffers without either the mixer or this
   *   file learning that two slugs can share one.
   */
  const urlFor = (slug, variant, key) => {
    const def = mix[slug];
    if (!def) return '';
    const a = assets[def.src ?? slug];
    const n = a?.files?.length ?? 0;
    if (!n) return '';
    let i = usable(variant) ? Math.floor(variant) : num(mixer.variantFor?.(slug, key ?? ''), 0);
    if (!usable(i)) i = 0;
    i = ((i % n) + n) % n;
    return `${base}/${a.files[i]}`;
  };

  /** @param {string} slug @returns {string} */
  const busOf = (slug) => {
    const b = mix[slug]?.bus;
    return b === 'ui' || b === 'ambient' ? b : 'sfx';
  };

  /** @param {string} slug @param {any} key @returns {string} The loops-map key. */
  const loopKey = (slug, key) => `${slug}|${key === undefined || key === null ? '' : key}`;

  // ── the slot book-keeping ───────────────────────────────────────────────────

  /**
   * Claims a mixer slot for `rec`. ⚠ If the slot was already ours the mixer has
   * just STOLEN it, so the previous voice is faded out here — that is where a
   * steal becomes audible, and it must be a fade: see {@link STEAL_FADE}.
   * @param {VoiceRec} rec
   */
  const install = (rec) => {
    const prev = records.get(rec.voice);
    if (prev && prev !== rec) {
      prev.fading = true;
      if (prev.loopId && loops.get(prev.loopId) === prev) loops.delete(prev.loopId);
      try {
        prev.v?.stop(STEAL_FADE);
      } catch (e) {
        warn('sfx: stealing a voice threw', e);
      }
    }
    records.set(rec.voice, rec);
  };

  /**
   * The backend voice ended (finished, was stopped, or was stolen).
   * ⚠ THE IDENTITY GUARD: the slot is only released if the record still in the
   * map is this one. A stolen voice's `onEnded` arrives AFTER its slot has been
   * re-let, and releasing then would free somebody else's sound.
   * @param {VoiceRec} rec
   */
  const onEnded = (rec) => {
    if (records.get(rec.voice) === rec) {
      records.delete(rec.voice);
      // Harmless if the mixer already reaped this slot in `update` — `release`
      // on a FREE voice is a no-op, and the alternative is a second book of
      // voice state over here.
      try {
        mixer.release?.(rec.voice);
      } catch (e) {
        warn('sfx: mixer.release threw', e);
      }
    }
    if (rec.loopId && loops.get(rec.loopId) === rec) loops.delete(rec.loopId);
  };

  /**
   * The live gain and pan for a record, asked of the MIXER — the distance law
   * lives in exactly one place (`attenuate`/`panFor`) and this file does no
   * arithmetic on metres.
   *
   * ⚠ A 2D sound is passed `undefined` rather than the listener's own position:
   * `gainPanFor` documents that a call with no usable position returns the mix
   * gain and centre pan, which is the 2D answer BY DEFINITION rather than by an
   * arithmetic accident at distance zero. Handing it the listener would make the
   * rain bed's level depend on `refDist`, `maxDist` and `cullFade` — three
   * fields the rain row does not even set.
   *
   * @param {VoiceRec} rec
   * @returns {{gain: number, pan: number}}
   */
  const livePan = (rec) => {
    const gp = rec.positional
      ? mixer.gainPanFor?.(rec.x, rec.y, rec.z, rec.slug)
      : mixer.gainPanFor?.(undefined, undefined, undefined, rec.slug);
    // ⚠ No answer means LEAVE IT ALONE — `rec.gain` already carries `userGain`,
    // so folding the multiplier into a fallback would square it once a frame.
    if (!gp || !usable(gp.gain)) return { gain: rec.gain, pan: rec.pan };
    return {
      gain: Math.max(0, gp.gain) * rec.userGain,
      pan: rec.positional ? num(gp.pan, rec.pan) : 0,
    };
  };

  /**
   * Re-evaluates one live loop against the listener as it stands now.
   * ⚠ Writes only on a real change: a parked car's engine should cost zero
   * scheduled ramps per frame, and a ramp re-scheduled every frame never
   * completes.
   * @param {VoiceRec} rec
   */
  const refreshLoop = (rec) => {
    if (rec.fading || !rec.v?.playing) return;
    const { gain, pan } = livePan(rec);
    if (Math.abs(gain - rec.gain) > EPS) {
      rec.gain = gain;
      rec.v.setGain(gain, TRACK_RAMP);
    }
    if (rec.positional && Math.abs(pan - rec.pan) > EPS) {
      rec.pan = pan;
      rec.v.setPan(pan, TRACK_RAMP);
    }
  };

  /**
   * ⚠⚠ **A STEAL DOES NOT ALWAYS RE-LET THE SLOT IT TOOK, AND THIS IS WHAT
   * CLOSES THAT GAP.**
   *
   * `mixer.request` frees its victim and then allocates `_firstFree()`, which is
   * the victim's slot only when the POOL was full. The other two steals — the
   * per-slug cap (`gunshot` at `maxVoices` 5, `phone_ring` at 1) and the loop
   * budget — can fire with free slots below the victim's index, and then the
   * mixer has forgotten a voice whose source is still running down here. Left
   * alone that is a second phone ringing over the first, and a stolen siren that
   * plays until its slot happens to be re-let, which for a loop is never.
   *
   * `install()` catches the re-let case; this catches the rest, once per frame:
   * a record whose slot the mixer says is FREE has been stolen — UNLESS its own
   * source was simply due to finish, which is the ordinary reap and must be left
   * to end on its own tail rather than be cut short. `endsAt` is that
   * discriminator and it is measured from the DECODED BUFFER, not mirrored from
   * the mixer's `_durOf` (the `links.js`/`countryroads.js` trap: a second copy
   * of a duration rule that has to agree).
   *
   * @param {number} t
   */
  const sweepStolen = (t) => {
    if (typeof mixer.stateOf !== 'function') return; // an older mixer: no sweep, no harm
    for (const rec of [...records.values()]) {
      if (rec.fading || !rec.v?.playing) continue;
      if (mixer.stateOf(rec.voice) !== VOICE.FREE) continue;
      if (t >= rec.endsAt) continue; // the ordinary reap — let the tail ring out
      rec.fading = true;
      records.delete(rec.voice);
      if (rec.loopId && loops.get(rec.loopId) === rec) loops.delete(rec.loopId);
      try {
        rec.v.stop(STEAL_FADE);
      } catch (e) {
        warn('sfx: stopping a stolen voice threw', e);
      }
    }
  };

  /** Pushes {@link level} (and {@link muted}) onto the graph. */
  const applyLevels = (ramp = LEVEL_RAMP) => {
    if (!enabled) return;
    backend.setBusLevel('master', muted ? 0 : level.master, ramp);
    backend.setBusLevel('sfx', level.sfx, ramp);
    backend.setBusLevel('ambient', level.ambient, ramp);
    backend.setBusLevel('ui', level.ui, ramp);
    backend.setBusLevel('music', level.music, ramp);
  };

  /**
   * Every clip the table can reach, tagged with its bus so the backend can decode
   * `ui` first. Built once, at construction — it is pure data over content that
   * never changes, and it costs one walk of ~17 slugs.
   * @returns {Array<{url: string, bus: string, loop: boolean}>}
   */
  const manifest = () => {
    /** @type {Map<string, {url: string, bus: string, loop: boolean}>} */
    const out = new Map();
    for (const slug of Object.keys(mix)) {
      const def = mix[slug];
      const a = assets[def?.src ?? slug];
      if (!a?.files?.length) continue;
      const bus = busOf(slug);
      const loop = a.kind === 'loop';
      for (const f of a.files) {
        const url = `${base}/${f}`;
        const hit = out.get(url);
        // ⚠ An aliased clip (`explosion` borrows `car_crash`) can be reached
        // through two slugs on two buses. It is ONE file, so it takes the
        // EARLIER tier — decoding it late because its second owner is ambient
        // would be the alias silently costing latency.
        if (!hit) out.set(url, { url, bus, loop });
        else if (hit.bus !== 'ui' && bus === 'ui') hit.bus = 'ui';
      }
    }
    return [...out.values()];
  };

  const clips = enabled ? manifest() : [];

  // ── the API ─────────────────────────────────────────────────────────────────

  const api = {
    name: 'sfx',

    // ── one-shots ─────────────────────────────────────────────────────────────

    /**
     * Play one sound. ⚠ **NEVER THROWS AND NEVER AWAITS.** A missing slug, an
     * undecoded buffer, a suspended context, a full voice pool, a distance past
     * `maxDist`: all of them are `-1` and none of them is an error. The callers
     * are `GameAudio.js`'s wiring table, firing from inside the fixed step.
     *
     * @param {string} slug
     * @param {object} [opts] `{x, y, z, key, gain, rate, priority}` — see the
     *   contract. Omitting x/y/z makes it 2D: no distance cull, centre pan.
     * @returns {number} A voice id, or -1.
     */
    play(slug, opts) {
      if (!enabled || disposed) return -1;
      const op = opts ?? NO_OPTS;
      const t = now();

      /** @type {any} */
      let r = null;
      try {
        r = mixer.request(slug, op, t);
      } catch (e) {
        warn(`sfx: mixer.request('${slug}') threw`, e);
        return -1;
      }
      if (!r) return -1; // culled, dropped, gapped or unknown — the mixer counted it

      const voice = Math.floor(num(r.voice, -1));
      if (voice < 0) return -1;

      const url = urlFor(slug, r.variant, op.key);
      if (!url) {
        mixer.release?.(voice);
        return -1;
      }

      const rate = Math.max(0.02, num(r.rate, 1));
      // The REAL length of the thing about to play, straight off the decoded
      // buffer — see `sweepStolen`, which is its only reader.
      const bufDur = num(backend.buffer(url)?.duration, 0);

      /** @type {VoiceRec} */
      const rec = {
        voice,
        slug,
        url,
        v: null,
        loopId: null,
        positional: isPositional(op),
        x: num(op.x, 0),
        y: num(op.y, 0),
        z: num(op.z, 0),
        userGain: Math.max(0, num(op.gain, 1)),
        gain: Math.max(0, num(r.gain, 1)),
        pan: num(r.pan, 0),
        endsAt: r.loop ? Infinity : t + bufDur / rate + 0.05,
        fading: false,
      };

      const v = backend.play({
        url,
        bus: busOf(slug),
        gain: rec.gain,
        pan: rec.pan,
        rate,
        loop: !!r.loop,
        fadeIn: 0, // a one-shot's own attack IS its fade in
        onEnded: () => onEnded(rec),
      });

      if (!v) {
        // No buffer yet, or no context. The mixer already allocated, so give the
        // slot straight back rather than leaving a silent voice holding it.
        mixer.release?.(voice);
        return -1;
      }

      rec.v = v;
      install(rec);
      return voice;
    },

    // ── loops ─────────────────────────────────────────────────────────────────

    /**
     * Start, or KEEP ALIVE, the loop identified by `(slug, key)`. ⚠ **IDEMPOTENT
     * — safe to call every frame**, which is how `GameAudio.js` drives a car's
     * engine: it does not track starts, it just says what is true this frame.
     *
     * ⚠ **A loop that stops being called does NOT expire.** `stopLoop` /
     * `stopAllLoops` / `dispose` are the only exits, and that is deliberate: the
     * rain bed is started once and runs for minutes, and a keep-alive timeout
     * would silently kill it. The mixer is still `touch`ed on every call so its
     * own age book-keeping stays honest.
     *
     * @param {string} slug
     * @param {any} key Entity key. `''`/null is a legal single global loop.
     * @param {object} [opts] As `play()`; `gain` and `rate` apply LIVE.
     */
    loop(slug, key, opts) {
      if (!enabled || disposed) return;
      const op = opts ?? NO_OPTS;
      const id = loopKey(slug, key);
      const t = now();

      const cur = loops.get(id);
      if (cur) {
        // Keep-alive: refresh the position, the caller's gain and the rate, then
        // let the normal per-frame path do the ramping. No new voice, no dice.
        cur.positional = isPositional(op) || cur.positional;
        if (usable(op.x)) cur.x = op.x;
        if (usable(op.y)) cur.y = op.y;
        if (usable(op.z)) cur.z = op.z;
        cur.userGain = Math.max(0, num(op.gain, cur.userGain));
        if (usable(op.rate)) cur.v?.setRate(Math.max(0.02, op.rate), TRACK_RAMP);
        try {
          mixer.touch?.(cur.voice, t);
        } catch (e) {
          warn('sfx: mixer.touch threw', e);
        }
        refreshLoop(cur);
        return;
      }

      /** @type {any} */
      let r = null;
      try {
        // ⚠ `loop: true` is passed IN: the mixer holds no asset kinds, so the
        // caller is the only one who can say that this allocation will hold a
        // node open. Its loop budget (`tune.maxLoops`) depends on it.
        r = mixer.request(slug, { ...op, key, loop: true }, t);
      } catch (e) {
        warn(`sfx: mixer.request('${slug}') threw`, e);
        return;
      }
      if (!r) return;

      const voice = Math.floor(num(r.voice, -1));
      if (voice < 0) return;

      const url = urlFor(slug, r.variant, key);
      if (!url) {
        mixer.release?.(voice);
        return;
      }

      /** @type {VoiceRec} */
      const rec = {
        voice,
        slug,
        url,
        v: null,
        loopId: id,
        positional: isPositional(op),
        x: num(op.x, 0),
        y: num(op.y, 0),
        z: num(op.z, 0),
        userGain: Math.max(0, num(op.gain, 1)),
        gain: Math.max(0, num(r.gain, 1)),
        pan: num(r.pan, 0),
        endsAt: Infinity, // a loop ends when somebody says so, never on a clock
        fading: false,
      };

      const v = backend.play({
        url,
        bus: busOf(slug),
        gain: rec.gain,
        pan: rec.pan,
        rate: Math.max(0.02, num(op.rate ?? r.rate, 1)),
        loop: true,
        // ⚠ A loop STARTS on a fade. A siren that appears at full level is a
        // step change with a five-second buffer behind it, which is the click
        // this whole system is built to avoid — and `tune.loopFade` is
        // documented as "never zero" for exactly this.
        fadeIn: loopFade,
        onEnded: () => onEnded(rec),
      });

      if (!v) {
        mixer.release?.(voice);
        return;
      }

      rec.v = v;
      install(rec);
      loops.set(id, rec);
    },

    /**
     * Fade out and release. ⚠ The record leaves {@link loops} IMMEDIATELY, so a
     * `loop()` for the same `(slug, key)` during the fade starts a fresh voice
     * and the two cross-fade — which is right, and is also why there is no
     * "resurrect a fading loop" branch to get wrong.
     * @param {string} slug @param {any} key
     */
    stopLoop(slug, key) {
      if (!enabled || disposed) return;
      const id = loopKey(slug, key);
      const rec = loops.get(id);
      if (!rec) return;
      loops.delete(id);
      rec.fading = true;
      try {
        // ⚠ `fade` before `stop`: the slot still holds a node for `loopFade`
        // seconds, so it must stay in the mixer's budgets — but it is now the
        // FIRST thing a steal takes, whatever its priority, because it is
        // already leaving. `release` follows from `onEnded`.
        mixer.fade?.(rec.voice);
        rec.v?.stop(loopFade);
      } catch (e) {
        warn('sfx: stopLoop threw', e);
      }
    },

    /**
     * @param {string} [slug] Only this slug's loops; omit for all of them.
     * ⚠ Iterates a SNAPSHOT — `stopLoop` mutates the map.
     */
    stopAllLoops(slug) {
      if (!enabled || disposed) return;
      for (const rec of [...loops.values()]) {
        if (slug !== undefined && slug !== null && rec.slug !== slug) continue;
        const id = rec.loopId;
        if (!id) continue;
        loops.delete(id);
        rec.fading = true;
        try {
          mixer.fade?.(rec.voice);
          rec.v?.stop(loopFade);
        } catch (e) {
          warn('sfx: stopAllLoops threw', e);
        }
      }
    },

    // ── music ─────────────────────────────────────────────────────────────────

    /**
     * THE RADIO'S WINDOW ONTO THE GRAPH — see the header: a track is STREAMED,
     * so it shares nothing with the voices above but the bus ladder.
     *
     * ⚠ THIS OBJECT HOLDS NO STATE AND MAKES NO DECISIONS. It does not know what
     * a station is, which track is on, whether one has finished, or how long the
     * player has been out of the car — `systems/Radio.js` owns all of that and
     * simply says "play this url from here". Two places deciding what is on air
     * would be the `hurtPlayer(amount, lethal)` family, and the model's clock is
     * the one that has to keep running while nobody is listening.
     */
    music: {
      /**
       * @param {string} url `base + file`, exactly as the model handed it over.
       *   ⚠ Do NOT re-derive it — a second copy of the path rule is the
       *   `links.js`/`countryroads.js` trap.
       * @param {number} [offset] Seconds in. A streamed element seeks, which is
       *   the whole "back in the car at 0:40" requirement, for free.
       * @param {object} [opts] `{gain, fade}` — see `backend.playMusic`.
       */
      play(url, offset = 0, opts) {
        if (!enabled || disposed) return;
        try {
          backend.playMusic(url, offset, opts);
        } catch (e) {
          warn('sfx: music play threw', e);
        }
      },

      /**
       * Fade out and pause. ⚠ Does NOT clear the position or the url — the
       * station stays on air while the player is out of the car, and the next
       * `play()` is told by the model where it has got to.
       * @param {number} [fade] Seconds. `RADIO_TUNE.fade` is what the wiring passes.
       */
      stop(fade) {
        if (!enabled || disposed) return;
        try {
          backend.stopMusic(fade);
        } catch (e) {
          warn('sfx: music stop threw', e);
        }
      },

      /**
       * The radio's own level, under the player's `music` slider. ⚠ Ramped, never
       * stepped, and STICKY across tracks — it is `RADIO_TUNE.gain`, a property of
       * the station. ⚠ It DEFAULTS TO 1, so a wiring that never calls this is
       * loud rather than silent; applying the tune is the wiring's job, because
       * the engine may not import `src/game/`.
       * @param {number} g
       */
      setGain(g) {
        if (!enabled || disposed) return;
        try {
          backend.setMusicGain(g);
        } catch (e) {
          warn('sfx: music setGain threw', e);
        }
      },

      /**
       * @returns {number} Seconds into the current track, or 0. ⚠ A READOUT, not
       *   the station's clock: this stops when the element pauses and the model's
       *   does not, which is the entire point of the model owning one.
       */
      get position() {
        return enabled && !disposed ? num(backend.musicPosition, 0) : 0;
      },

      /** @returns {boolean} */
      get playing() {
        return !!enabled && !disposed && !!backend.musicPlaying;
      },

      /** @returns {string|null} The url we were handed, or null. */
      get src() {
        return enabled && !disposed ? (backend.musicSrc ?? null) : null;
      },
    },

    // ── the listener ──────────────────────────────────────────────────────────

    /**
     * ⚠ **CALL THIS BEFORE ANY `play`/`loop` IN A FRAME.** Everything positional
     * is resolved against the listener as it stood when the sound was asked for,
     * so a stale listener pans the whole frame's gunfire at last frame's camera.
     *
     * ⚠ THE LISTENER IS THE PLAYER, NOT `interiors.focus` — a shop is a real room
     * 100 m under the island, and anchoring on the door ring would pipe the whole
     * street into it. `src/game/sfx.js` records the same rule beside
     * `teleportDist`, and `docs/dialogue.md` records it for the speech test.
     *
     * @param {number} x @param {number} y @param {number} z
     * @param {number} yaw CAMERA yaw, radians.
     */
    setListener(x, y, z, yaw) {
      lis.x = num(x, lis.x);
      lis.y = num(y, lis.y);
      lis.z = num(z, lis.z);
      lis.yaw = num(yaw, lis.yaw);
      if (!enabled || disposed) return;
      try {
        mixer.setListener?.(lis.x, lis.y, lis.z, lis.yaw);
      } catch (e) {
        warn('sfx: mixer.setListener threw', e);
      }
    },

    // ── levels ────────────────────────────────────────────────────────────────

    /**
     * @param {string} bus `'master'|'sfx'|'ambient'|'ui'|'music'`.
     * @param {number} v 0..1. ⚠ Ramped, never stepped — a slider dragged fast is
     *   a stream of small steps and a stream of small steps is a stream of small
     *   clicks.
     */
    setLevel(bus, v) {
      if (!(bus in level)) return;
      level[bus] = Math.max(0, Math.min(1, num(v, level[bus])));
      applyLevels();
    },

    /** @param {string} bus @returns {number} */
    getLevel(bus) {
      return num(level[bus], 0);
    },

    /**
     * @param {boolean} on ⚠ Mute is a MASTER GAIN of 0, not a suspend and not a
     *   per-level write: the levels must survive it untouched so unmuting is
     *   exact, and a suspended context would stop the loops' fades mid-ramp.
     */
    setMuted(on) {
      const next = !!on;
      if (next === muted) return;
      muted = next;
      applyLevels();
    },

    /** @returns {boolean} */
    get muted() {
      return muted;
    },

    // ── lifecycle ─────────────────────────────────────────────────────────────

    /**
     * Resume the context and start loading. ⚠ **CALL FROM A USER GESTURE.**
     * Idempotent, never rejects, and a no-op with no table.
     * @returns {Promise<void>}
     */
    unlock() {
      if (!enabled || disposed) return Promise.resolve();
      // Queue first: `preload` cannot decode before there is a context, so this
      // just fills the list and `unlock` pumps it.
      try {
        backend.preload(clips);
      } catch (e) {
        warn('sfx: preload threw', e);
      }
      return backend
        .unlock()
        .then(() => {
          if (disposed) return;
          applyLevels(0.01);
        })
        .catch((e) => {
          warn('sfx: unlock failed', e);
        });
    },

    /** @returns {boolean} Context running AND the first buffers decoded. */
    get ready() {
      return !!enabled && !disposed && !!backend.ready;
    },

    /**
     * Per frame. ⚠ **DRIVE THIS FROM THE RENDER HOOK, NOT THE FIXED STEP** — see
     * the header: five overlays can freeze the sim and a loop that stops with it
     * is a bug. `dt` is only the fallback clock's increment; the ramps and the
     * mixer's clock run on the audio context's own time.
     * @param {number} dt Seconds since the last call.
     */
    update(dt) {
      if (!enabled || disposed) return;
      if (usable(dt) && dt > 0) simT += dt;
      const t = now();
      try {
        mixer.update?.(t);
      } catch (e) {
        warn('sfx: mixer.update threw', e);
      }
      // ⚠ AFTER the reap, so a voice the mixer has just retired on its own clock
      // is seen with `endsAt` already passed and is left to finish, rather than
      // being read as a steal and cut off on its last frame.
      sweepStolen(t);
      for (const rec of loops.values()) refreshLoop(rec);
    },

    /** The game wants silence (a pause, a menu). Not the same as a hidden tab. */
    suspend() {
      if (!enabled || disposed) return;
      backend.suspend();
    },

    /** The game wants sound back. */
    resume() {
      if (!enabled || disposed) return;
      backend.resume();
    },

    /**
     * @returns {{voices: number, loops: number, stolen: number, culled: number,
     *   dropped: number, peak: number, decoded: number, pending: number,
     *   ready: boolean, ctx: string|null,
     *   music: {playing: boolean, src: string|null, position: number}}} The
     *   contract's shape. All zeros with no table, which is what a debug panel
     *   should show for a system that does not exist.
     */
    stats() {
      const m = (enabled && mixer.stats?.()) || null;
      const b = (enabled && backend.stats?.()) || null;
      return {
        voices: num(m?.live, 0),
        loops: num(m?.loops, loops.size),
        stolen: num(m?.stolen, 0),
        culled: num(m?.culled, 0),
        dropped: num(m?.dropped, 0),
        // ⚠ THE HIGH-WATER MARK IS THE ONLY FIELD HERE THAT SURVIVES THE MOMENT
        // YOU LOOK AWAY. `voices` is an instant, and a pool that briefly filled
        // during a firefight reads as 2 by the time anyone opens a stats panel —
        // so without `peak` the one question the pool size exists to answer
        // ("was 24 ever nearly enough?") cannot be asked after the fact. The
        // mixer has always tracked it; the facade simply was not forwarding it,
        // and `/play/sfx` had grown its own parallel copy on the fixed step.
        peak: num(m?.peak, 0),
        decoded: num(b?.decoded, 0),
        pending: num(b?.pending, 0),
        ready: api.ready,
        ctx: b?.ctx ?? null,
        // ⚠ A NESTED ROW, not three more top-level fields. Music shares none of
        // the counters above it — no slot, no priority, no pool — and flattening
        // it would invite the next reader to fold `position` into a voice count
        // or to read `playing` as "there is any sound at all".
        music: {
          playing: api.music.playing,
          src: api.music.src,
          position: api.music.position,
        },
      };
    },

    /**
     * Idempotent. Stops everything, closes the context and drops the visibility
     * listener. ⚠ After this every method is a no-op, so a system disposed
     * mid-frame cannot make a noise on its way out — the `Wallet#dispose` rule.
     */
    dispose() {
      if (disposed) return;
      disposed = true;
      loops.clear();
      records.clear();
      try {
        mixer?.dispose?.();
      } catch (e) {
        warn('sfx: mixer dispose threw', e);
      }
      try {
        backend?.dispose();
      } catch (e) {
        warn('sfx: backend dispose threw', e);
      }
    },
  };

  return api;
}
